/**
 * Password policy — hint copy + minimal client-side validation.
 *
 * ## Authority
 *
 * The REAL password policy is configured in **Supabase Auth →
 * Policies** in the project dashboard. That's the only place that
 * actually rejects weak passwords on submit (Supabase enforces
 * server-side, regardless of what our client sends), so it's the
 * only gate that can't be bypassed by a malicious client. Don't
 * try to re-implement the policy here — two sources of truth
 * drift, and we'd be lying to ourselves about the actual security
 * boundary.
 *
 * Current Supabase configuration (as of last review):
 *
 *   - Minimum length: 10
 *   - Required character classes: lowercase + uppercase + numbers
 *     + special characters
 *   - Leaked-password protection: NOT enabled — Supabase gated
 *     this behind a paid plan in 2025. Worth turning on if we
 *     ever bump to Pro: it's the single biggest security upgrade
 *     for the cost (catches passwords known from HaveIBeenPwned
 *     breaches at signup / change-password time). Until then,
 *     min-length + complexity is the only floor we enforce.
 *
 * If you update the dashboard, update PASSWORD_HINT below to
 * match — that's the only behaviour change needed at this layer.
 *
 * ## What this module provides
 *
 *   - {@link PASSWORD_HINT} — single source of the user-facing
 *     hint copy. Shown beneath every password input ("At least
 *     10 characters") so users know what to aim for before
 *     they hit submit and bounce off Supabase's error.
 *
 *   - {@link PASSWORD_MAX_LENGTH} — bcrypt silently truncates
 *     above 72 bytes; we cap at 64 well below that so multi-byte
 *     UTF-8 chars don't sneak past. This DOES need client-side
 *     enforcement because Supabase will accept the longer string
 *     and silently truncate, leaving the user with a password
 *     that doesn't match what they typed.
 *
 *   - {@link isPasswordPlausible} — local "did the user type
 *     anything non-trivial" check, used to gate the submit
 *     button so we don't fire a server round-trip on a blank
 *     input. Everything else (min length, complexity, breach
 *     check) is Supabase's job.
 *
 * ## What this module does NOT provide
 *
 *   - Min-length enforcement — that lives in Supabase. Adding it
 *     here would let the two drift; if the dashboard policy is
 *     bumped from 10 → 12, our gate would silently fail to track.
 *   - Complexity validation — same reason. Trust Supabase.
 *   - Strength-meter scoring — out of scope for MVP (zxcvbn is
 *     ~400KB; not worth the bundle cost until user data shows
 *     people struggling to pick a password).
 */

/**
 * User-facing hint string. Surfaced below every password input.
 * KEEP IN SYNC with the Supabase dashboard's minimum-length
 * setting — Supabase will reject mismatched submissions
 * server-side, but we want users to know what's expected up
 * front rather than after a network round-trip.
 */
export const PASSWORD_HINT = "At least 10 characters";

/**
 * Upper bound enforced by us (not Supabase). bcrypt operates on
 * 72 bytes max and silently truncates; cap at 64 chars to stay
 * well below 72 even with UTF-8 multi-byte chars (a 64-char
 * password of pure ASCII is 64 bytes; pure 4-byte emoji would be
 * 256 bytes, so this isn't a hard guarantee for adversarial
 * inputs, but covers the realistic case).
 */
export const PASSWORD_MAX_LENGTH = 64;

/**
 * Plausibility check — non-empty after trim. The minimum bar
 * before we'll round-trip to Supabase for the real policy check.
 * Pure-whitespace inputs are rejected here so the user sees an
 * immediate "type something" cue rather than a confusing
 * Supabase error.
 */
export function isPasswordPlausible(pw: string): boolean {
  return pw.trim().length > 0;
}
